import React from 'react';
import type { IIconProps, IconName } from './Icon.type';

const rawIcons = import.meta.glob('../../assets/icons/*.svg', { eager: true, import: 'default' });

const ICONS: Record<IconName, string> = Object.fromEntries(
  Object.entries(rawIcons).map(([path, src]) => {
    const name = path.split('/').pop()!.replace('.svg', '');
    return [name as IconName, src as string];
  })
) as Record<IconName, string>;

export const Icon: React.FC<IIconProps> = ({
  name,
  size,
  className,
  spin = false,
  variant,
  inheritColor = false,
}: IIconProps) => {

  const sizeMap = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
    xxl: 'w-10 h-10',
  };

  const variantColorMap = {
    success: 'text-fg-success-primary',
    error: 'text-fg-error-primary',
    warning: 'text-fg-warning-primary',
    secondary: 'text-fg-secondary',
    'brand-secondary': 'text-fg-brand-secondary',
    selected: 'text-text-brand-primary',
    brand: 'text-border-brand',
    'utility-gray': 'text-utility-gray-400',
    'brand-utility': 'text-utility-brand-700',
    'brand-icon': 'text-border-brand-icn',
  };

  const IconSrc = ICONS[name];
  let sizeClass = '';

  if (size) {
    sizeClass = sizeMap[size];
  }
  if (!IconSrc) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  const spinClass = spin ? 'animate-spin' : '';
  const variantClass = variant ? variantColorMap[variant] : '';

  // Use mask approach for variant or inheritColor
  const useMask = variant || inheritColor;

  return (
    <span
      className={`inline-flex items-center justify-center ${sizeClass} ${spinClass} ${variantClass} ${className ?? ''}`}
      style={useMask ? { 
        WebkitMaskImage: `url(${IconSrc})`,
        maskImage: `url(${IconSrc})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        backgroundColor: 'currentColor',
      } : undefined}
    >
      {!useMask && (
        <img 
          src={IconSrc} 
          alt={`${name} icon`}
          className="w-full h-full"
        />
      )}
    </span>
  );
};
